/**
 * Facebook OAuth Service
 * Handles OAuth 2.0 flow for Facebook and Instagram Business accounts
 */

/**
 * Initiate Facebook OAuth flow with popup window
 * @param {string} clientId - The client ID from our platform
 * @param {'facebook'|'instagram'} connectionType - Connection mode requested by user
 */

import {
  FACEBOOK_APP_ID,
  FACEBOOK_OAUTH_REDIRECT_URI,
  FACEBOOK_OAUTH_GET_PAGES,
  FACEBOOK_OAUTH_EXCHANGE
} from '../utils/constants';

export const initiateFacebookOAuth = (
  clientId,
  connectionType = 'facebook'
) => {
  const appId = FACEBOOK_APP_ID;
  const redirectUri = FACEBOOK_OAUTH_REDIRECT_URI;

  // Facebook OAuth scopes
  const scopes = [
    'pages_show_list', // View list of pages
    'pages_read_engagement', // Read page engagement
    'pages_read_user_content', // Read user content (e.g. reviews)
    'pages_manage_posts', // Publish to pages
    'pages_manage_metadata', // Edit page settings like phone, website, location
    'pages_manage_engagement', // Manage likes, comments, and replies (Required for review replies)
    'instagram_basic', // Basic Instagram access
    'instagram_content_publish', // Publish to Instagram
    'business_management' // Access to business accounts
  ].join(',');

  // Build OAuth URL with state parameter
  const state = btoa(JSON.stringify({ clientId, connectionType }));

  const authUrl =
    `https://www.facebook.com/v18.0/dialog/oauth?` +
    `client_id=${appId}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&scope=${encodeURIComponent(scopes)}` +
    `&response_type=code` +
    `&auth_type=rerequest` +
    `&state=${encodeURIComponent(state)}`;

  console.log('Opening Facebook OAuth popup:', authUrl);

  // Configuración de la ventana flotante
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popupFeatures = `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`;

  // Abre ventana flotante para autorización de Facebook
  const popup = window.open(authUrl, 'FacebookOAuth', popupFeatures);

  if (popup) {
    popup.focus();
  }
};

/**
 * Extract authorization code and state from URL
 */
export const extractAuthorizationCode = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');
  const errorMessage = urlParams.get('error_message');
  const errorCode = urlParams.get('error_code');

  if (error) {
    const parts = [errorDescription || errorMessage || error];
    if (errorCode) {
      parts.push(`code=${errorCode}`);
    }

    return {
      success: false,
      error: parts.join(' | ')
    };
  }

  if (!code) {
    return {
      success: false,
      error: 'No authorization code received'
    };
  }

  // Decode state to get clientId and requested connection type
  let clientId = null;
  let connectionType = 'facebook';
  try {
    const normalizedState = decodeURIComponent(state || '').replace(/ /g, '+');
    const stateObj = JSON.parse(atob(normalizedState));
    clientId = stateObj.clientId;
    connectionType = stateObj.connectionType || 'facebook';
  } catch (e) {
    console.error('Failed to parse state:', e);
  }

  return {
    success: true,
    code,
    clientId,
    connectionType
  };
};

/**
 * Exchange authorization code for access token
 * @param {string} apiEndpoint - Backend API endpoint
 * @param {string} code - Authorization code from Facebook
 * @param {string} clientId - Client ID from our platform
 */
export const exchangeCodeForTokens = async (apiEndpoint, code, clientId) => {
  try {
    const redirectUri = FACEBOOK_OAUTH_REDIRECT_URI;

    const response = await fetch(`${FACEBOOK_OAUTH_EXCHANGE || apiEndpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code,
        clientId,
        redirectUri
      })
    });

    let data;
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }

    if (!response.ok) {
      const errorMessage =
        data.details ||
        data.error ||
        data.message ||
        'Failed to exchange code for tokens';

      return {
        success: false,
        error: errorMessage,
        status: response.status,
        data
      };
    }

    const defaultPagesEndpoint = apiEndpoint.replace(
      '/facebook/exchange-code',
      '/facebook/get-pages'
    );
    const pagesEndpoint = FACEBOOK_OAUTH_GET_PAGES || defaultPagesEndpoint;

    const pagesResponse = await getFacebookPages(
      FACEBOOK_OAUTH_GET_PAGES || pagesEndpoint,
      clientId
    );
    const pages = pagesResponse.success ? pagesResponse.pages || [] : [];

    return {
      success: true,
      data: {
        ...data,
        pages
      }
    };
  } catch (error) {
    console.error('Error exchanging code:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      isNetworkError: true
    };
  }
};

/**
 * Get Facebook pages and Instagram accounts
 * @param {string} apiEndpoint - Backend API endpoint
 * @param {string} clientId - Client ID from our platform
 */
export const getFacebookPages = async (apiEndpoint, clientId) => {
  try {
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        clientId
      })
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to fetch Facebook pages'
      };
    }

    return {
      success: true,
      pages: data.pages || [],
      count: data.count || 0
    };
  } catch (error) {
    console.error('Error fetching pages:', error);
    return {
      success: false,
      error: error.message || 'Network error'
    };
  }
};

/**
 * Save selected Facebook page or Instagram account
 * @param {string} apiEndpoint - Backend API endpoint
 * @param {string} clientId - Client ID from our platform
 * @param {object} page - Selected page/Instagram account object
 */
export const saveFacebookPage = async (
  apiEndpoint,
  clientId,
  page,
  connectionType = 'facebook'
) => {
  try {
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        clientId,
        page,
        connectionType
      })
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to save page'
      };
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Error saving page:', error);
    return {
      success: false,
      error: error.message || 'Network error'
    };
  }
};
